import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { line, Color, ITEM_IMAGE_SIZES, ITEM_SUPPLIER_STATUSES, Size, byteText, fitWithin, imageBoxFor, isItemImageMime, suggestionRowsFor, t, type ItemImageSizeKey, type ItemSupplierStatus, type SuggestionRow, type SuggestionSource } from '@bms2/shared';
import { Button, Dropdown, RadioGroup, Select, TextInput } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { FormFrame } from '../../components/forms/FormFrame';
import { useCompanySearch } from '../settings/api';
import { AddAttributeTypeWindow } from '../settings/AttributeTypeForm';
import { STOCK_SITES_PATH, apiFetch, del, patch, post, upload, useApi } from './api';
import styles from './RecordPage.module.css';
import { useAddNew } from './AddNewHost';

/**
 * The Item page's own modals (piece 17): an attribute from the category's
 * list, a supplier as a dated link with its vendor part number and minimum,
 * and a site the Item is stocked at with its low-stock judgement. Each writes
 * through the catalog's routes of piece 16 and reloads the page.
 */
export const CATALOG_ITEMS_PATH = '/catalog/items';
export const CATALOG_SUPPLIERS_PATH = '/catalog/suppliers';
export const CATALOG_SITES_PATH = '/catalog/sites';

interface ItemModalProps {
  open: boolean;
  itemId: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}

const NEW_TYPE = '__new__';

interface AttributeOptions {
  rows: { attributeTypeId: string; attributeName: string; kind: string | null; family: string | null; values: string[]; inherited: boolean; isActive?: boolean }[];
  types: { id: string; attributeName: string; kind: string }[];
}

interface AttributeTypeDetail {
  id: string;
  kind: string;
  units: { id: string; label: string }[];
  values: { id: string; value: string }[];
}

interface AttributeRow {
  id: string;
  attributeTypeId: string;
  unitId: string | null;
  enteredValue: number | null;
  attributeTypeValueId: string | null;
  attributeType: { attributeName: string; kind: string };
}

/** A Measured value enters in any unit of the type's family and stores in the base unit; an Enumerated value picks its row (decision 0168). */
export function ItemAttributeModal({ open, itemId, categoryId, attributeId = null, apiBaseUrl = '', onCancel, onSaved }: ItemModalProps & { categoryId: string | null; attributeId?: string | null }) {
  // An existing row loads the form (piece 17, Tile 5); the modal waits for it rather than opening empty and filling in.
  const held = useApi<{ attributes: AttributeRow[] }>(open && attributeId ? `${CATALOG_ITEMS_PATH}/${itemId}` : null, apiBaseUrl);
  const row = attributeId && held.state.kind === 'ready' ? (held.state.data.attributes.find((r) => r.id === attributeId) ?? null) : null;
  if (attributeId && !row) return null;
  return <AttributeForm key={row?.id ?? 'new'} open={open} itemId={itemId} categoryId={categoryId} initial={row} apiBaseUrl={apiBaseUrl} onCancel={onCancel} onSaved={onSaved} />;
}

function AttributeForm({ open, itemId, categoryId, initial, apiBaseUrl = '', onCancel, onSaved }: ItemModalProps & { categoryId: string | null; initial: AttributeRow | null }) {
  const options = useApi<AttributeOptions>(open && categoryId && !initial ? `/catalog/categories/${categoryId}/attributes` : null, apiBaseUrl);
  const [typeId, setTypeId] = useState(initial?.attributeTypeId ?? '');
  const detail = useApi<AttributeTypeDetail>(open && typeId ? `/catalog/attribute-types/${typeId}` : null, apiBaseUrl);
  const [unitId, setUnitId] = useState(initial?.unitId ?? '');
  const [value, setValue] = useState(initial?.enteredValue === null || initial?.enteredValue === undefined ? '' : String(initial.enteredValue));
  const [valueId, setValueId] = useState(initial?.attributeTypeValueId ?? '');
  const [error, setError] = useState<string | null>(null);
  // 🔨 The picker's last row makes the type that is not there yet, through the same form Settings draws, and comes back with it chosen (product owner, 2026-09-10).
  const [creating, setCreating] = useState(false);
  const answer = options.state.kind === 'ready' ? options.state.data : null;
  // The category's own list first, then every other Measured or Enumerated type. 🔒 A retired type leaves the offer while an item that carries it keeps reading it (decision 10090; T3499).
  const offered = [...(answer?.rows ?? []).filter((r) => r.isActive !== false).map((r) => ({ id: r.attributeTypeId, label: `${r.attributeName} · ${t('item.attribute.category')}` })), ...(answer?.types ?? []).filter((type) => !(answer?.rows ?? []).some((r) => r.attributeTypeId === type.id)).map((type) => ({ id: type.id, label: type.attributeName }))];
  const kind = detail.state.kind === 'ready' ? detail.state.data.kind : null;
  // The leave question stands only once something was chosen or changed (decision 3061).
  const typed = initial ? unitId !== (initial.unitId ?? '') || valueId !== (initial.attributeTypeValueId ?? '') || value !== (initial.enteredValue === null ? '' : String(initial.enteredValue)) : typeId !== '';
  const save = async () => {
    setError(null);
    const body = kind === 'Enumerated' ? { attributeTypeId: typeId, attributeTypeValueId: valueId || null } : { attributeTypeId: typeId, unitId: unitId || null, enteredValue: value.trim() === '' ? null : Number(value) };
    const result = await post(`${CATALOG_ITEMS_PATH}/${itemId}/attributes`, body, apiBaseUrl);
    if (!result.ok) return setError(result.message);
    onSaved();
    onCancel();
  };
  const remove = async () => {
    if (!initial) return;
    const result = await del(`${CATALOG_ITEMS_PATH}/${itemId}/attributes/${initial.id}`, apiBaseUrl);
    if (!result.ok) return setError(result.message);
    onSaved();
    onCancel();
  };
  return (
    <>
    {/* 🔨 Unmounted when closed, so each opening starts empty (decision 2803; T3499). */}
    {open && creating ? <AddAttributeTypeWindow apiBaseUrl={apiBaseUrl} onClose={() => setCreating(false)} onCreated={(row) => { setCreating(false); options.reload(); setTypeId(row.id); setUnitId(''); setValueId(''); }} /> : null}
    <FormFrame
      host="window"
      open={open && !creating}
      title={initial ? t('item.attribute.editTitle') : t('item.attribute.title')}
      onCancel={onCancel}
      dataForm="item-attribute"
      typed={typed}
      refusal={error}
      onDismissRefusal={() => setError(null)}
      act={{ label: t('modal.save'), onAct: () => save(), disabled: typeId === '', disabledReason: t('item.attribute.pickType') }}
    >
      {initial ? (
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="attribute-type">{t('item.attribute.type')}</label>
          <p className={styles.hint} id="attribute-type" data-item-field="attributeType">{initial.attributeType.attributeName}</p>
        </div>
      ) : (
        <Select size={Size.Large} label={t('item.attribute.type')} value={typeId} onChange={(chosen) => { if (chosen === NEW_TYPE) { setCreating(true); return; } setTypeId(chosen); setUnitId(''); setValueId(''); }} placeholder={t('edit.none')} options={[...offered.map((row) => ({ value: row.id, label: row.label })), { value: NEW_TYPE, label: t('item.attribute.newType') }]} dataAttribute={{ 'data-item-field': 'attributeType' }} />
      )}
      {kind === 'Enumerated' && detail.state.kind === 'ready' ? (
        <Select size={Size.Medium} label={t('item.attribute.value')} value={valueId} onChange={setValueId} placeholder={t('edit.none')} options={detail.state.data.values.map((row) => ({ value: row.id, label: row.value }))} dataAttribute={{ 'data-item-field': 'attributeValue' }} />
      ) : null}
      {kind === 'Measured' && detail.state.kind === 'ready' ? (
        <>
          <TextInput size={Size.Small} label={t('item.attribute.value')} value={value} onChange={setValue} name="enteredValue" />
          <Select size={Size.Medium} label={t('item.attribute.unit')} value={unitId} onChange={setUnitId} placeholder={t('edit.none')} options={detail.state.data.units.map((row) => ({ value: row.id, label: row.label }))} dataAttribute={{ 'data-item-field': 'attributeUnit' }} />
        </>
      ) : null}
      {initial ? (
        <div className={styles.actions}>
          <Button
            size={Size.Small}
            color={Color.Destructive}
            text={[t('item.attribute.remove')]}
            actions={[() => void remove()]}
            confirm={{ title: t('item.attribute.removeTitle'), consequence: { body: t('item.attribute.removeBody') }, confirmLabel: t('item.attribute.removeConfirm') }}
          />
        </div>
      ) : null}
    </FormFrame>
    </>
  );
}

/** A supplier is a dated link with many rows per Item (decision 0152); the manufacturer appears as one when the business buys direct. */
/**
 * A supplier row: added with its vendor, or edited in place for its part
 * number, its minimum and its status (decisions 0296, 0337); the vendor itself
 * is added, never changed. A status set on an ended row reopens it.
 */
export interface SupplierEdit {
  id: string;
  vendorName: string;
  vendorPartNumber: string | null;
  minimumOrderQuantity: number | null;
  status: ItemSupplierStatus;
  ended: boolean;
}

const statusLabelKey: Record<ItemSupplierStatus, 'item.supplier.status.primary' | 'item.supplier.status.active' | 'item.supplier.status.notApplicable' | 'item.supplier.status.blacklisted'> = {
  Primary: 'item.supplier.status.primary',
  Active: 'item.supplier.status.active',
  NotApplicable: 'item.supplier.status.notApplicable',
  Blacklisted: 'item.supplier.status.blacklisted',
};

export function ItemSupplierModal({ open, itemId, initial = null, apiBaseUrl = '', onCancel, onSaved }: ItemModalProps & { initial?: SupplierEdit | null }) {
  const addNew = useAddNew(apiBaseUrl);
  const search = useCompanySearch(apiBaseUrl);
  const [company, setCompany] = useState<SuggestionRow | null>(null);
  const [vendorPartNumber, setVendorPartNumber] = useState(initial?.vendorPartNumber ?? '');
  const [minimum, setMinimum] = useState(initial?.minimumOrderQuantity === null || initial?.minimumOrderQuantity === undefined ? '' : String(initial.minimumOrderQuantity));
  const [status, setStatus] = useState<ItemSupplierStatus>(initial?.status ?? 'Active');
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setError(null);
    const body = { vendorPartNumber: vendorPartNumber.trim() || null, minimumOrderQuantity: minimum.trim() === '' ? null : Number(minimum), status };
    if (initial) {
      const result = await patch(`${CATALOG_SUPPLIERS_PATH}/${initial.id}`, body, apiBaseUrl);
      if (!result.ok) return setError(result.message);
    } else {
      if (!company) return setError(t('item.supplier.pickCompany'));
      const result = await post(`${CATALOG_ITEMS_PATH}/${itemId}/suppliers`, { companyId: company.id, ...body }, apiBaseUrl);
      if (!result.ok) return setError(result.message);
    }
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={initial ? t('item.supplier.editTitle') : t('item.supplier.title')} onCancel={onCancel} dataModal="item-supplier" actions={[{ kind: 'primary', label: t('modal.save'), onAct: () => save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      {initial ? (
        <p className={styles.hint} data-supplier-editing={initial.id}>{line(t('item.supplier.company'), '·', initial.vendorName)}</p>
      ) : (
        <Dropdown size={Size.Medium} label={t('item.supplier.company')} entityLabel={t('edit.companyEntity')} search={search} value={company?.line1 ?? null} onSelect={setCompany} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord', role: 'vendor' }, setCompany)} />
      )}
      {initial?.ended ? <p className={styles.hint} data-supplier-reopen="true">{t('item.supplier.reopenHint')}</p> : null}
      <TextInput size={Size.Medium} label={t('item.supplier.vendorPartNumber')} value={vendorPartNumber} onChange={setVendorPartNumber} name="vendorPartNumber" />
      <TextInput size={Size.Medium} label={t('item.supplier.minimum')} value={minimum} onChange={setMinimum} name="minimumOrderQuantity" />
      {/* Primary is exclusive per Item: the API demotes the previous Primary to Active on save (decision 0337). */}
      <RadioGroup name="supplierStatus" legend={t('item.supplier.status')} orientation="horizontal" value={status} onChange={(next) => setStatus(next as ItemSupplierStatus)} options={ITEM_SUPPLIER_STATUSES.map((row) => ({ value: row, label: t(statusLabelKey[row]) }))} />
      {addNew.host}
    </Modal>
  );
}

/** A row's existence is what makes the Item stocked at a site; the quantity is derived from the ledger and never typed here (decision 0122). */
export function ItemSiteStockModal({ open, itemId, apiBaseUrl = '', onCancel, onSaved }: ItemModalProps) {
  const addNew = useAddNew(apiBaseUrl);
  const [site, setSite] = useState<SuggestionRow | null>(null);
  const [threshold, setThreshold] = useState('0');
  const [reorder, setReorder] = useState('');
  const [error, setError] = useState<string | null>(null);
  // 🔒 Our own sites alone: a row here is what makes the Item stocked somewhere, and a customer's site is not a shelf (decision 0451).
  const searchSites = async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
    const response = await apiFetch(`${apiBaseUrl}${STOCK_SITES_PATH}?q=${encodeURIComponent(query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: SuggestionSource[] };
    return suggestionRowsFor('SiteRecord', body.rows);
  };
  const save = async () => {
    setError(null);
    if (!site) return setError(t('item.stock.pickSite'));
    const result = await post(`${CATALOG_SITES_PATH}/${site.id}/stock`, { itemId, lowStockThreshold: Number(threshold) || 0, reorderQuantity: reorder.trim() === '' ? null : Number(reorder) }, apiBaseUrl);
    if (!result.ok) return setError(result.message);
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={t('item.stock.title')} onCancel={onCancel} dataModal="item-site-stock" actions={[{ kind: 'primary', label: t('modal.save'), onAct: () => save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <p className={styles.hint}>{t('item.stock.hint')}</p>
      <Dropdown size={Size.Medium} label={t('item.stock.site')} entityLabel={t('item.stock.siteEntity')} search={searchSites} value={site?.line1 ?? null} onSelect={setSite} onAddNew={() => addNew.open({ kind: 'site' }, setSite)} />
      <TextInput size={Size.Small} label={t('item.stock.threshold')} value={threshold} onChange={setThreshold} name="lowStockThreshold" />
      <TextInput size={Size.Medium} label={t('item.stock.reorder')} value={reorder} onChange={setReorder} name="reorderQuantity" />
      {addNew.host}
    </Modal>
  );
}

/**
 * The Item's one image (decision 0269). 🔨 The browser scales the picked
 * picture into the chosen printed size before the upload, so a phone photo
 * never crosses the wire at full size; the server scales it again into the
 * three-inch cap, which is the guard. The resulting pixel size and bytes stand
 * beside the preview before Save.
 */
interface ScaledImage {
  blob: Blob;
  width: number;
  height: number;
}

const sizeLabelKey: Record<ItemImageSizeKey, 'item.image.size.three' | 'item.image.size.two' | 'item.image.size.one'> = {
  three: 'item.image.size.three',
  two: 'item.image.size.two',
  one: 'item.image.size.one',
};

/** Scales in the browser through a canvas; answers null where the browser has no bitmap support, and the original goes up for the server to scale. */
export async function scaleImageInBrowser(file: File, maxPx: number): Promise<ScaledImage | null> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return null;
  try {
    const bitmap = await createImageBitmap(file);
    const box = fitWithin(bitmap.width, bitmap.height, maxPx);
    const canvas = document.createElement('canvas');
    canvas.width = box.width;
    canvas.height = box.height;
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, box.width, box.height);
    const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.88));
    return blob ? { blob, width: box.width, height: box.height } : null;
  } catch {
    // silent: a picture the browser cannot redraw is refused as one it cannot read.
    return null;
  }
}

/**
 * 🔒 Where the picture goes and what the window calls it. The Item's own by
 * default; the asset's picture reuses this window with its own path and
 * wording (decision 1291), because the pick, the scale and the save are the
 * same and a second window would be one screen kept in two places.
 */
export interface ImageWindowTarget {
  uploadPath?: string;
  title?: string;
  alt?: string;
  dataModal?: string;
  /**
   * 🔒 Edit Selected's Set image (decision 7640): the window picks and scales the
   * picture and hands it back, and uploads nothing; the bulk edit sends it once
   * to every ticked Item. The button reads `saveLabel`.
   */
  onPick?: (picture: { blob: Blob; name: string }) => void;
  saveLabel?: string;
}

export function ItemImageModal({ open, itemId, apiBaseUrl = '', onCancel, onSaved, uploadPath, title, alt, dataModal = 'item-image', onPick, saveLabel }: ItemModalProps & ImageWindowTarget) {
  const [picked, setPicked] = useState<File | null>(null);
  const [scaled, setScaled] = useState<ScaledImage | null>(null);
  const [size, setSize] = useState<ItemImageSizeKey>('three');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inches = ITEM_IMAGE_SIZES.find((row) => row.key === size)?.inches ?? 3;

  // The scaling is asynchronous, so the effect only ever writes from its callback; the "nothing picked" case is cleared by the picker itself.
  useEffect(() => {
    if (!picked) return undefined;
    let cancelled = false;
    void scaleImageInBrowser(picked, imageBoxFor(inches)).then((result) => {
      if (!cancelled) setScaled(result);
    });
    return () => {
      cancelled = true;
    };
  }, [picked, inches]);

  const source = scaled?.blob ?? picked;
  const preview = useMemo(() => (source && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(source) : null), [source]);
  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  const pick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setError(null);
    setScaled(null);
    if (file && !isItemImageMime(file.type)) {
      setPicked(null);
      setError(t('item.image.notImage'));
      return;
    }
    setPicked(file);
  };

  const save = async () => {
    setError(null);
    if (!picked) return setError(t('item.image.pickFirst'));
    if (onPick) {
      onPick({ blob: scaled?.blob ?? picked, name: picked.name });
      onCancel();
      return;
    }
    setSaving(true);
    const form = new FormData();
    form.append('file', scaled?.blob ?? picked, picked.name);
    form.append('printedInches', String(inches));
    const result = await upload(uploadPath ?? `${CATALOG_ITEMS_PATH}/${itemId}/image`, form, apiBaseUrl);
    setSaving(false);
    if (!result.ok) return setError(result.message);
    onSaved();
    onCancel();
  };

  const storedText = scaled
    ? line(t('item.image.storedAt'), `${scaled.width} × ${scaled.height} px ·`, byteText(scaled.blob.size), picked ? `(${t('item.image.was')} ${byteText(picked.size)})` : null)
    : picked
      ? line(byteText(picked.size))
      : null;

  return (
    <Modal open={open} title={title ?? t('item.image.title')} onCancel={onCancel} dataModal={dataModal} actions={[{ kind: 'primary', label: saveLabel ?? t('item.image.save'), onAct: () => save(), disabled: saving || !picked }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <label className={styles.field}>
        <span className={styles.fieldLabel}>{t('item.image.pick')}</span>
        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={pick} data-item-image-input="true" />
      </label>
      <RadioGroup name="printedSize" legend={t('item.image.size')} orientation="horizontal" value={size} onChange={(next) => setSize(next as ItemImageSizeKey)} options={ITEM_IMAGE_SIZES.map((row) => ({ value: row.key, label: t(sizeLabelKey[row.key]) }))} />
      <p className={styles.hint}>{t('item.image.hint')}</p>
      {preview ? (
        <div className={styles.imageBox} data-item-image-preview="true">
          <img src={preview} alt={alt ?? t('item.image.alt')} />
        </div>
      ) : null}
      {storedText ? (
        <p className={styles.hint} data-item-image-result="true">
          {storedText}
        </p>
      ) : null}
    </Modal>
  );
}

/** The image on the General Information tile with Set, Replace and Remove (piece 17; decision 0269). */
export function ItemImageBlock({ itemId, imageFileId, apiBaseUrl = '', onSet, onRemoved, onError }: { itemId: string; imageFileId: string | null; apiBaseUrl?: string; onSet: () => void; onRemoved: () => void; onError: (message: string) => void }) {
  const remove = async () => {
    const result = await del(`${CATALOG_ITEMS_PATH}/${itemId}/image`, apiBaseUrl);
    if (!result.ok) return onError(result.message);
    onRemoved();
  };
  return (
    <div className={styles.itemImage} data-item-image={imageFileId ?? 'none'}>
      {imageFileId ? (
        <div className={styles.imageBox}>
          <img src={`${apiBaseUrl}/files/${imageFileId}/stream`} alt={t('item.image.alt')} onError={(event) => { event.currentTarget.hidden = true; }} />
        </div>
      ) : (
        <p className={styles.hint}>{t('item.image.none')}</p>
      )}
      <div className={styles.actions}>
        <Button size={Size.Small} color={Color.Navy} text={[imageFileId ? t('item.image.replace') : t('item.image.set')]} actions={[onSet]} />
        {imageFileId ? (
          <Button
            size={Size.Small}
            color={Color.Destructive}
            text={[t('item.image.remove')]}
            actions={[() => void remove()]}
            confirm={{ title: t('item.image.removeTitle'), consequence: { body: t('item.image.removeBody') }, confirmLabel: t('item.image.removeConfirm') }}
          />
        ) : null}
      </div>
    </div>
  );
}
